import type { PackId } from './tiers'

export interface Bonus {
  n: number
  title: string
  blurb: string
  volume: string
  href: string
  /* which artwork in BONUS_COVERS this card uses. It is stated rather than
     derived from n, because two packs left this list and became paid
     unlocks and the numbering closed up behind them. */
  img: string
  /* true for anything hosted as a single PDF of ours rather than a Drive folder */
  own?: boolean
}

const F = (id: string) => `https://drive.google.com/drive/folders/${id}`

/* Included with every order, at every price. Eight, not ten: the papercraft
   megapack and the handwriting programme moved into the locked stack below. */
export const bonuses: Bonus[] = [
  {
    n: 1,
    title: '130 Cognitive Flashcards',
    blurb:
      'Print and laminate. Emotions, animal sounds, the alphabet and first sensory words, across several themed decks.',
    volume: '130 cards',
    img: '02',
    href: F('1eUTff0cbe990EL8Rq8Ax1p6iCvbal1lW'),
  },
  {
    n: 2,
    title: "The Little Chef's Math Menu",
    blurb:
      'Your child runs a restaurant. Counting, money and problem-solving hidden inside cooking play.',
    volume: 'Full bundle',
    img: '04',
    href: F('1qy2zvJlqoHgkiBIIqyNJyiJTKjoNb1EL'),
  },
  {
    n: 3,
    title: 'The 3D Farm & Wearable Watch',
    blurb:
      'Build a paper farm and a watch your child can actually wear. Flat sheets that end up off the table.',
    volume: 'Full bundle',
    img: '05',
    href: F('1j047o_ojqjvd1o474AMMoG9OaCarfpbc'),
  },
  {
    n: 4,
    title: '360° Brain Stimulation Program',
    blurb:
      'Mazes, matching grids and pattern work, ordered by what a child can handle at each stage.',
    volume: 'Full programme',
    img: '06',
    href: F('1C_QWA9PzAtGdrF4zQu0OALo5_Dm-Z1x8'),
  },
  {
    n: 5,
    title: 'Early Genius Development Playkit',
    blurb:
      'Sorting, pairing and spotting the odd one out. The groundwork for everything in the twelve books.',
    volume: 'Full playkit',
    img: '07',
    href: F('1EdHmgQfwjpbbxci6KRlyOgvPq7E37AhM'),
  },
  {
    n: 6,
    title: 'My Very First Speech & Language Book',
    blurb:
      'An illustrated guide built around first words, sounds and telling a story out loud together.',
    volume: 'Illustrated book',
    img: '08',
    href: F('1KvpDtfw-ugfJYf9OQyJlU82JWdxbo46t'),
  },
  {
    n: 7,
    title: 'Early Speech & Vocabulary Starter',
    blurb:
      'Prompts and short exercises for everyday conversational play, written with speech therapists.',
    volume: 'Starter pack',
    img: '09',
    href: F('1lTBDbfgi7GB6erL4OJabw576G27Y_fNT'),
  },
  {
    n: 8,
    title: 'Family Bible Activities & Values',
    blurb:
      'Illustrated retellings and value-focused worksheets, written for time together rather than quiet time.',
    volume: 'Workbook',
    img: '10',
    href: F('1h6Tn01ZboJQ9bi59NYHDsurRG49RLZwd'),
  },
]

/* ---------------------------------------------------------------------------
   THE LOCKED STACK

   These are not bonuses, they are products. Somebody who did not buy them sees
   the cover, the real volume and a price. Somebody who did arrives through a
   delivery link carrying ?unlock= and finds the same card already open, with
   nothing to type.

   The titles here must match the product names in ImpulTienda exactly, or a
   customer sees one name on the receipt and another on the card.
--------------------------------------------------------------------------- */
export interface LockedPack {
  id: PackId
  title: string
  blurb: string
  volume: string
  img: string
  href: string
}

export const lockedPacks: LockedPack[] = [
  {
    id: 'megapack',
    title: 'MegaPack Creative Hands',
    blurb:
      'Print, fold, play. Animals, dinosaurs, geometric solids and whole fantasy worlds, all from flat sheets of paper. The single biggest library in the collection.',
    volume: '1,200+ printable files',
    img: '01',
    href: F('1g30OTr2TV42Dz4VRfafXsnno1OjT8ctb'),
  },
  {
    id: 'calligraphy',
    title: 'Mindful Handwriting & Calligraphy',
    blurb:
      'Tracing, letter-building and early typography, with calming colouring pages between the drills. The quiet half hour that goes with the building.',
    volume: '56+ sheets',
    img: '03',
    href: F('1SGdo-rlbyAn1mAQoRutvRR-3bwiqGNYf'),
  },
  {
    id: 'videos',
    title: 'The Video Workshop',
    blurb:
      'The trickiest builds, filmed start to finish, so your child can follow along without you reading the steps out loud. New builds added every month.',
    volume: 'Video library',
    img: '',
    href: '',
  },
]

export interface AgeLibrary {
  id: string
  band: string
  title: string
  blurb: string
  href: string
  /* which age bands of the 366 projects this maps onto */
  filter: string[]
}

export const ageLibraries: AgeLibrary[] = [
  {
    id: 'toddlers',
    band: '1 to 3',
    title: 'Toddlers',
    blurb: 'Sensory play and first motor skills. Below the age the twelve books start at.',
    href: F('1-oQZ-aG4nlZACdlRkYgJ60qJe2D_R4ow'),
    filter: [],
  },
  {
    id: 'preschool',
    band: '2 to 6',
    title: 'Preschoolers',
    blurb: 'Logic, tracing and cutting. Where the first books start to become possible.',
    href: F('14865096aMrTRaes4EvIHc_pNkPHNRMvv'),
    filter: ['6–9'],
  },
  {
    id: 'elementary',
    band: '7 to 12',
    title: 'School age',
    blurb: 'Maths, science and puzzles, alongside every project in the twelve books.',
    href: F('1YoMgIw2tYIhdUBqdhzuoEEjjlI1YI2pV'),
    filter: ['7–10', '8–11', '9–12'],
  },
]
